import React from 'react';
import { UserCheck, Loader2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { moduleApi } from '../../lib/api';
import { creneauxSeChevauchent, libelleCreneau } from '../../../shared/creneaux.js';

interface ExtraTypeFlat { id: string; label: string; partnerId: string; }
interface InternalType { id: string; label: string; partner_name?: string; employees: { employee_id: string; first_name: string; last_name: string }[]; }
interface ExternalPerson { id: string; first_name: string; last_name: string; already_used: boolean; }
interface Selection {
    uid: string; kind: 'external' | 'internal';
    partnerId: string; extraTypeId: string; personIds: string[];
    internalTypeId: string; employeeIds: string[];
    // Sous-groupe horaire externe distinct du reste du besoin (ex: 2 hôtesses à 18h, 2 autres à 19h) —
    // undefined = sous-groupe "par défaut" (hérite de l'horaire du type / de l'événement), demande d'Emeni
    // (2026-08-22). Porté par les DEUX genres depuis le 2026-08-28 : un salarié interne est booké sur un
    // créneau précis, plus sur le poste en vrac — sans quoi on ne sait pas à quelle heure il vient, et rien
    // n'empêche d'en mettre quatre sur un créneau qui n'en demande que deux.
    slotUid?: string;
}
interface ExternalSlot { uid: string; typeId: string; qty: number; start: string; end: string; }

/** Modale dédiée au booking des extras (demande d'Emeni : "Booker" ne doit plus ouvrir 'Modifier la
 *  Privatisation').
 *
 *  PARTAGE DES ROLES (arbitrage d'Emeni, 2026-08-26) — c'est la règle qui structure tout cet écran :
 *  le BESOIN (combien de personnes, à quels horaires, découpé ou non en sous-groupes) appartient à la
 *  personne qui DEMANDE le booking, et se saisit dans « Modifier la Privatisation ». Le BOOKER, lui, ne
 *  fait que désigner QUI vient : il réserve les salariés internes et recommande des externes au partenaire.
 *  Il ne peut donc ni changer une quantité, ni changer un horaire, ni ajouter ou retirer un sous-groupe —
 *  ces champs sont affichés en lecture seule. Le backend applique déjà cette règle depuis le 2026-08-25
 *  (cette modale n'envoie plus aucun `extras_needs`) ; l'interface s'y conforme désormais aussi, au lieu
 *  de promettre une modification qui n'aurait jamais été enregistrée.
 *
 *  La section "extras externe" d'un type ne s'affiche que si le besoin n'est pas déjà entièrement couvert
 *  par les salariés internes sélectionnés (remainingForType(t) > 0), sous le titre "Quels extras
 *  aimeriez-vous sur cet événement ?" demandé par Emeni. */
export function BookExtrasModal({ event, onClose, onSaved }: { event: any; onClose: () => void; onSaved: () => void }) {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const [allExtraTypesFlat, setAllExtraTypesFlat] = React.useState<ExtraTypeFlat[]>([]);
    const [extrasInternalTypes, setExtrasInternalTypes] = React.useState<InternalType[]>([]);
    const [extrasPeopleCache, setExtrasPeopleCache] = React.useState<Record<string, ExternalPerson[]>>({});
    const [extrasSelections, setExtrasSelections] = React.useState<Selection[]>([]);
    const [extrasNeeds, setExtrasNeeds] = React.useState<Record<string, number>>({});
    // Horaire saisi par le demandeur (pas modifiable ici) — juste reporté tel quel à la sauvegarde pour ne
    // pas l'effacer, et affiché en info pour que le booker sache quand les extras doivent être présents.
    const [extrasTimeOverrides, setExtrasTimeOverrides] = React.useState<Record<string, { start: string | null; end: string | null }>>({});
    const [committedExternalByType, setCommittedExternalByType] = React.useState<Record<string, number>>({});
    const [autoSendExtras, setAutoSendExtras] = React.useState(!!event.auto_send_extras);
    // Sous-groupes horaires du besoin, en LECTURE SEULE ici : reflet de ce que le demandeur a saisi, jamais
    // modifiable depuis cette modale (voir le partage des rôles en tête de fichier). Le sous-groupe "par
    // défaut" n'a pas d'entrée ici : il vit dans une Selection sans slotUid, comme avant.
    const [extraExternalSlots, setExtraExternalSlots] = React.useState<ExternalSlot[]>([]);

    const loadExtrasPeople = React.useCallback((partnerId: string, extraTypeId: string) => {
        moduleApi.getExtrasPartnerPeople(partnerId, extraTypeId).then((d: any) => {
            setExtrasPeopleCache((prev) => ({ ...prev, [extraTypeId]: Array.isArray(d) ? d : [] }));
        }).catch(() => {});
    }, []);

    React.useEffect(() => {
        (async () => {
            try {
                const [partners, internalTypes, existing] = await Promise.all([
                    moduleApi.getExtrasPartners().catch(() => []),
                    moduleApi.getExtrasInternalTypes().catch(() => []),
                    moduleApi.getEvenementielExtras(event.id).catch(() => null),
                ]);

                const flat: ExtraTypeFlat[] = (Array.isArray(partners) ? partners : []).flatMap((p: any) =>
                    (p.types || []).filter((t: any) => t.scope_evenementiel !== false && t.scope_evenementiel !== 0)
                        .map((t: any) => ({ id: t.id, label: `${t.label} (${p.name})`, partnerId: p.id }))
                );
                setAllExtraTypesFlat(flat);
                const internal = (Array.isArray(internalTypes) ? internalTypes : []).filter((t: any) => Array.isArray(t.employees) && t.employees.length > 0);
                setExtrasInternalTypes(internal);
                flat.forEach((t) => loadExtrasPeople(t.partnerId, t.id));

                const committedMap: Record<string, number> = {};
                (existing?.committed_external || []).forEach((r: any) => { committedMap[r.extra_type_id] = Number(r.quantity) || 0; });
                setCommittedExternalByType(committedMap);

                // Renvoyé brut par ligne (jamais fusionné par type côté backend) — un type peut désormais être
                // réparti par le demandeur, dès la création du besoin, en plusieurs sous-groupes horaires (ex:
                // 1 hôtesse à 18h, 1 autre à 19h — demande d'Emeni 2026-08-22). La 1ère ligne de chaque type
                // est la ligne "par défaut" (déjà affichée en horaire ci-dessus) ; les suivantes deviennent des
                // sous-groupes pré-configurés, affichés ici directement — le booker n'a plus besoin de les
                // recréer à la main, seulement de choisir qui pour chacun.
                const needsData = existing?.needs || event.extras_needs || [];
                const needsByType = new Map<string, any[]>();
                needsData.forEach((n: any) => {
                    if (!needsByType.has(n.extra_type_id)) needsByType.set(n.extra_type_id, []);
                    needsByType.get(n.extra_type_id)!.push(n);
                });
                const timeMap: Record<string, { start: string | null; end: string | null }> = {};
                const needsMap: Record<string, number> = {};
                const predefinedSlots: ExternalSlot[] = [];
                needsByType.forEach((rows, typeId) => {
                    const [first, ...rest] = rows;
                    timeMap[typeId] = { start: first.start_time_override || null, end: first.end_time_override || null };
                    needsMap[typeId] = rows.reduce((sum, r) => sum + (Number(r.needed_count) || 0), 0);
                    rest.forEach((r: any) => {
                        predefinedSlots.push({ uid: `slot${Date.now()}${Math.random().toString(36).slice(2, 6)}`, typeId, qty: Number(r.needed_count) || 0, start: r.start_time_override || '', end: r.end_time_override || '' });
                    });
                });
                setExtrasTimeOverrides(timeMap);
                setExtrasNeeds(needsMap);

                // L'horaire par défaut d'un type = son horaire propre (saisi par le demandeur), sinon celui de
                // l'événement — sert à distinguer, à la réouverture, le sous-groupe "par défaut" (horaire =
                // horaire par défaut du type) des sous-groupes explicitement différents (horaire distinct).
                const eventStart = event.start_time ? String(event.start_time).slice(11, 16) : '';
                const eventEnd = event.end_time ? String(event.end_time).slice(11, 16) : '';
                const defaultTimeForType = (typeId: string) => ({
                    start: timeMap[typeId]?.start || eventStart,
                    end: timeMap[typeId]?.end || eventEnd,
                });

                const externalSelections: Selection[] = [];
                const slots: ExternalSlot[] = [...predefinedSlots];
                (existing?.external || []).forEach((r: any) => {
                    const def = defaultTimeForType(r.extra_type_id);
                    const rStart = r.start_time || '';
                    const rEnd = r.end_time || '';
                    const isDefault = rStart === def.start && rEnd === def.end;
                    if (isDefault) {
                        externalSelections.push({
                            uid: `x${Date.now()}${Math.random().toString(36).slice(2, 6)}`, kind: 'external' as const,
                            partnerId: r.partner_id, extraTypeId: r.extra_type_id, personIds: r.person_ids || [],
                            internalTypeId: '', employeeIds: [],
                        });
                        return;
                    }
                    // Retrouve le sous-groupe pré-configuré (extras_event_needs) correspondant à cet horaire,
                    // sinon en crée un ad-hoc (ex: réservation déjà faite avant cette fonctionnalité).
                    let matchedSlot = slots.find((s) => s.typeId === r.extra_type_id && s.start === rStart && s.end === rEnd);
                    if (!matchedSlot) {
                        matchedSlot = { uid: `slot${Date.now()}${Math.random().toString(36).slice(2, 6)}`, typeId: r.extra_type_id, qty: Math.max(Number(r.quantity) || 0, (r.person_ids || []).length), start: rStart, end: rEnd };
                        slots.push(matchedSlot);
                    }
                    externalSelections.push({
                        uid: `x${Date.now()}${Math.random().toString(36).slice(2, 6)}`, kind: 'external' as const,
                        partnerId: r.partner_id, extraTypeId: r.extra_type_id, personIds: r.person_ids || [],
                        internalTypeId: '', employeeIds: [], slotUid: matchedSlot.uid,
                    });
                });
                setExtraExternalSlots(slots);
                // Les internes deja bookes reviennent PRE-SELECTIONNES, ranges dans leur creneau : c'est ce
                // qui permet d'en retirer un qui se desiste sans defaire le reste. Les bookings anterieurs a
                // cette version portent l'horaire de l'evenement et non celui d'un sous-groupe : faute de
                // correspondance, ils retombent sur le creneau par defaut plutot que de disparaitre.
                const internalByKey = new Map<string, { typeId: string; slotUid?: string; employeeIds: string[] }>();
                (existing?.internal || []).forEach((r: any) => {
                    const rStart = r.start_time || '';
                    const rEnd = r.end_time || '';
                    const matched = slots.find((s) => s.typeId === r.extra_type_id && s.start === rStart && s.end === rEnd);
                    const slotUid = matched?.uid;
                    const cle = `${r.extra_type_id}::${slotUid || ''}`;
                    if (!internalByKey.has(cle)) internalByKey.set(cle, { typeId: r.extra_type_id, slotUid, employeeIds: [] });
                    internalByKey.get(cle)!.employeeIds.push(r.employee_id);
                });
                const internalSelections: Selection[] = Array.from(internalByKey.values()).map(({ typeId, slotUid, employeeIds }) => ({
                    uid: `x${Date.now()}${Math.random().toString(36).slice(2, 6)}`, kind: 'internal' as const,
                    partnerId: '', extraTypeId: '', personIds: [],
                    internalTypeId: typeId, employeeIds, slotUid,
                }));
                setExtrasSelections([...externalSelections, ...internalSelections]);
            } catch (e: any) {
                setError(e?.message || 'Erreur lors du chargement.');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event.id]);

    // Horaire de l'evenement : c'est l'horaire du creneau "par defaut" quand le poste n'en porte pas un
    // qui lui soit propre.
    const eventStartTime = event.start_time ? String(event.start_time).slice(11, 16) : '';
    const eventEndTime = event.end_time ? String(event.end_time).slice(11, 16) : '';

    const getNeededCount = (typeId: string) => extrasNeeds[typeId] || 0;
    const assignedCountByType = React.useMemo(() => {
        const map = new Map<string, number>();
        extrasSelections.forEach((s) => { if (s.kind === 'internal' && s.internalTypeId) map.set(s.internalTypeId, (map.get(s.internalTypeId) || 0) + s.employeeIds.length); });
        return map;
    }, [extrasSelections]);
    const remainingForType = (typeId: string) => Math.max(0, getNeededCount(typeId) - (assignedCountByType.get(typeId) || 0));
    const slotsForType = (typeId: string) => extraExternalSlots.filter((s) => s.typeId === typeId);
    const slotsQtySum = (typeId: string) => slotsForType(typeId).reduce((sum, s) => sum + (s.qty || 0), 0);
    // Ce qui reste pour le sous-groupe "par défaut" une fois les autres sous-groupes horaires déduits — les
    // sous-groupes additionnels ont chacun leur propre quantité explicite, le défaut absorbe le reste.
    const defaultSlotQty = (typeId: string) => Math.max(0, remainingForType(typeId) - slotsQtySum(typeId));
    // Ce qui est DEMANDE sur le creneau par defaut, sans rien deduire. A ne pas confondre avec
    // defaultSlotQty, qui dit ce qu'il RESTE a pourvoir : les salaries internes deja selectionnes en sont
    // retires. L'en-tete « Horaires demandes » utilisait cette seconde valeur et faisait donc disparaitre
    // un creneau entier des qu'il etait couvert en interne — sur « 2 hotesses a 12h + 2 a minuit », en
    // selectionnant 2 internes on ne voyait plus que le creneau de minuit. « On ne comprend rien de ce qui
    // est demande » (Emeni, 2026-08-28) : la ligne censee decrire la demande decrivait le reliquat.
    const demandeParDefaut = (typeId: string) => Math.max(0, getNeededCount(typeId) - slotsQtySum(typeId));

    // ── Le créneau devient l'unité de compte ──────────────────────────────────────────────────────
    // « Si 2 sont demandées de 18h à 02h, on ne devrait pouvoir sélectionner que 2 internes, puis ajouter
    // les 2 autres de 20h à 02h » (Emeni, 2026-08-28). Le plafond portait jusqu'ici sur le besoin TOTAL du
    // poste : rien n'empêchait d'entasser les quatre sur un seul créneau.
    const cleCreneau = (typeId: string, slotUid?: string) => `${typeId}::${slotUid || ''}`;
    const horairesDuCreneau = (typeId: string, slotUid?: string) => {
        if (slotUid) {
            const s = extraExternalSlots.find((x) => x.uid === slotUid);
            return { start: s?.start || '', end: s?.end || '' };
        }
        return {
            start: extrasTimeOverrides[typeId]?.start || eventStartTime,
            end: extrasTimeOverrides[typeId]?.end || eventEndTime,
        };
    };
    const qteDuCreneau = (typeId: string, slotUid?: string) =>
        slotUid ? (extraExternalSlots.find((s) => s.uid === slotUid)?.qty || 0) : demandeParDefaut(typeId);
    const internesDuCreneau = (typeId: string, slotUid?: string) =>
        extrasSelections.find((s) => s.kind === 'internal' && s.internalTypeId === typeId && (s.slotUid || undefined) === (slotUid || undefined))?.employeeIds || [];

    /** Le créneau déjà occupé par ce salarié et qui recouvre celui-ci, ou null s'il est libre. Le contrôle
     *  porte sur TOUTE la privatisation, tous postes confondus (choix d'Emeni) : c'est la même personne
     *  physique, elle ne peut pas être hôtesse ici et vestiaire là au même moment. */
    const creneauEnConflit = (employeeId: string, typeId: string, slotUid?: string): string | null => {
        const cible = horairesDuCreneau(typeId, slotUid);
        for (const s of extrasSelections) {
            if (s.kind !== 'internal' || !s.internalTypeId) continue;
            if (!s.employeeIds.includes(employeeId)) continue;
            if (s.internalTypeId === typeId && (s.slotUid || undefined) === (slotUid || undefined)) continue;
            const autre = horairesDuCreneau(s.internalTypeId, s.slotUid);
            if (creneauxSeChevauchent(cible, autre)) return libelleCreneau(autre.start, autre.end);
        }
        return null;
    };

    // Jamais plus de sélections (interne ou externe) que le besoin réel du type — demande d'Emeni : "je ne
    // devrais pas pouvoir sélectionner plus que ce qui est demandé". Le plafond ne bloque que l'AJOUT d'une
    // nouvelle pastille ; en retirer une reste toujours possible.
    const toggleExternalPerson = (typeId: string, partnerId: string, personId: string, slotUid?: string) => {
        setExtrasSelections((prev) => {
            const existing = prev.find((s) => s.kind === 'external' && s.extraTypeId === typeId && (s.slotUid || undefined) === (slotUid || undefined));
            const alreadySelected = existing?.personIds.includes(personId) ?? false;
            const cap = slotUid ? (extraExternalSlots.find((s) => s.uid === slotUid)?.qty || 0) : defaultSlotQty(typeId);
            if (!alreadySelected && (existing?.personIds.length || 0) >= cap) return prev;
            if (!existing) {
                return [...prev, { uid: `x${Date.now()}${Math.random().toString(36).slice(2, 6)}`, kind: 'external' as const, partnerId, extraTypeId: typeId, personIds: [personId], internalTypeId: '', employeeIds: [], slotUid }];
            }
            const nextPersonIds = alreadySelected ? existing.personIds.filter((id) => id !== personId) : [...existing.personIds, personId];
            if (nextPersonIds.length === 0) return prev.filter((s) => s.uid !== existing.uid);
            return prev.map((s) => s.uid === existing.uid ? { ...s, personIds: nextPersonIds } : s);
        });
    };
    const toggleInternalEmployee = (typeId: string, employeeId: string, slotUid?: string) => {
        setExtrasSelections((prev) => {
            const existing = prev.find((s) => s.kind === 'internal' && s.internalTypeId === typeId && (s.slotUid || undefined) === (slotUid || undefined));
            const alreadySelected = existing?.employeeIds.includes(employeeId) ?? false;
            // Retirer reste toujours possible : c'est ce qui permet de traiter un desistement sans avoir a
            // defaire toute la privatisation.
            if (!alreadySelected) {
                if ((existing?.employeeIds.length || 0) >= qteDuCreneau(typeId, slotUid)) return prev;
                if (creneauEnConflit(employeeId, typeId, slotUid)) return prev;
            }
            if (!existing) {
                return [...prev, { uid: `x${Date.now()}${Math.random().toString(36).slice(2, 6)}`, kind: 'internal' as const, partnerId: '', extraTypeId: '', personIds: [], internalTypeId: typeId, employeeIds: [employeeId], slotUid }];
            }
            const nextEmployeeIds = alreadySelected ? existing.employeeIds.filter((id) => id !== employeeId) : [...existing.employeeIds, employeeId];
            if (nextEmployeeIds.length === 0) return prev.filter((s) => s.uid !== existing.uid);
            return prev.map((s) => s.uid === existing.uid ? { ...s, employeeIds: nextEmployeeIds } : s);
        });
    };

    // Ajout / retrait / modification d'un sous-groupe horaire : retires de cette modale (2026-08-26).
    // Le booker ne definit plus le besoin, il ne fait que designer qui vient. Ces gestes restent entiers
    // dans la fenetre « Modifier la Privatisation », cote demandeur.

    const typesWithNeed = allExtraTypesFlat.filter((t) => getNeededCount(t.id) > 0);
    const internalTypesWithNeed = extrasInternalTypes.filter((t) => getNeededCount(t.id) > 0);
    // Un type externe n'a plus rien à proposer une fois son besoin entièrement couvert par l'interne —
    // demande d'Emeni ("si j'ai sélectionné 4 en interne sur 4 demandés, il ne propose pas les externes").
    const externalTypesToShow = typesWithNeed.filter((t) => remainingForType(t.id) > 0);

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            // L'horaire du CRENEAU accompagne chaque booking. Sans lui, le serveur retombait sur celui de
            // l'evenement : sur une privatisation de 19h a 23h avec des hotesses demandees de 18h a 02h, les
            // salaries etaient enregistres 19h-02h — un horaire que personne n'avait demande.
            const internal_extras = extrasSelections
                .filter((s) => s.kind === 'internal' && s.internalTypeId)
                .flatMap((s) => {
                    const h = horairesDuCreneau(s.internalTypeId, s.slotUid);
                    return s.employeeIds.map((employeeId) => ({
                        extra_type_id: s.internalTypeId,
                        employee_id: employeeId,
                        start_time_override: h.start || undefined,
                        end_time_override: h.end || undefined,
                    }));
                });

            // Une ligne extras_requests par sous-groupe (le "par défaut" + un par horaire additionnel) —
            // chacune avec sa propre quantité, son horaire propre, et sa propre suggestion de personnes.
            const extras_requests: any[] = [];
            for (const typeId of Object.keys(extrasNeeds)) {
                const flatType = allExtraTypesFlat.find((t) => t.id === typeId);
                if (!flatType) continue;
                const defaultQty = defaultSlotQty(typeId);
                if (defaultQty > 0) {
                    const suggestion = extrasSelections.find((s) => s.kind === 'external' && s.extraTypeId === typeId && !s.slotUid);
                    extras_requests.push({
                        partner_id: suggestion?.partnerId || flatType.partnerId,
                        extra_type_id: typeId,
                        quantity: Math.max(defaultQty, suggestion?.personIds.length || 0),
                        person_ids: suggestion && suggestion.personIds.length > 0 ? suggestion.personIds : undefined,
                        // Passé explicitement (plutôt que de laisser le backend retrouver l'horaire du type) —
                        // avec plusieurs lignes de besoin désormais possibles par type, seule cette modale sait
                        // laquelle est la ligne "par défaut".
                        start_time_override: extrasTimeOverrides[typeId]?.start || undefined,
                        end_time_override: extrasTimeOverrides[typeId]?.end || undefined,
                        // Rang 0 = l'horaire principal du poste. Mémorisé sur la demande pour que corriger
                        // plus tard l'horaire d'un sous-groupe ne recale que ses propres demandes.
                        need_slot_index: 0,
                    });
                }
                slotsForType(typeId).forEach((slot, i) => {
                    const suggestion = extrasSelections.find((s) => s.kind === 'external' && s.extraTypeId === typeId && s.slotUid === slot.uid);
                    const qty = Math.max(slot.qty || 0, suggestion?.personIds.length || 0);
                    if (qty <= 0) return;
                    extras_requests.push({
                        partner_id: suggestion?.partnerId || flatType.partnerId,
                        extra_type_id: typeId,
                        quantity: qty,
                        person_ids: suggestion && suggestion.personIds.length > 0 ? suggestion.personIds : undefined,
                        start_time_override: slot.start || undefined,
                        end_time_override: slot.end || undefined,
                        // Rang tiré de la POSITION dans la liste, pas d'un compteur qui avancerait au fil des
                        // lignes retenues : les sous-groupes pré-remplis depuis le besoin arrivent en tête et
                        // dans le même ordre qu'en base, leur rang doit donc rester 1, 2, 3… même si l'un
                        // d'eux est vide et ne donne aucune demande.
                        need_slot_index: i + 1,
                    });
                });
            }

            // AUCUN extras_needs envoyé volontairement : le besoin (combien de personnes, à quels horaires)
            // appartient au demandeur, pas au booker — cette modale ne fait que choisir QUI vient sur chaque
            // sous-groupe. En repassant un besoin, elle écrasait la répartition en sous-groupes par un seul
            // total par poste : "2 hôtesses 12h-18h + 2 hôtesses 18h-00h" redevenait "4 hôtesses 12h-18h",
            // et les 2 hôtesses du soir disparaissaient de la privatisation. Le backend distingue désormais
            // "aucun besoin transmis" (ne touche à rien) de "besoin vide" (efface tout).

            // Le PUT /evenementiel/:id est un remplacement complet (pas un patch partiel) : espaces/staff/
            // affectations sont supprimés puis réinsérés depuis le body, et les champs coeur (téléphone,
            // email, noms...) sont écrasés par ce qui est envoyé. On repasse donc systématiquement les valeurs
            // déjà connues de l'événement, jamais touchées ici, pour ne rien effacer par omission.
            const payload = {
                calendar_id: event.calendar_id,
                type: event.type,
                phone: event.phone,
                email: event.email,
                first_name: event.first_name,
                last_name: event.last_name,
                company_name: event.company_name,
                organizer_name: event.organizer_name,
                start_time: event.start_time,
                end_time: event.end_time,
                num_people: event.num_people,
                space_ids: Array.isArray(event.spaces) ? event.spaces.map((s: any) => s.id) : [],
                staff_requests: Array.isArray(event.staff) ? Object.fromEntries(event.staff.map((s: any) => [s.staff_type_id, s.count])) : {},
                assignments: Array.isArray(event.assignments) ? event.assignments.map((a: any) => ({ employee_id: a.employee_id, staff_type_id: a.staff_type_id })) : [],
                taken_by_id: event.taken_by_id || null,
                crm_contact_id: event.crm_contact_id || null,
                extras_requests,
                internal_extras,
                auto_send_extras: autoSendExtras,
                extras_booked: true,
            };

            const res: any = await moduleApi.updateEvenementiel(event.id, payload);
            if (Array.isArray(res?.over_committed) && res.over_committed.length > 0) {
                const lines = res.over_committed.map((o: any) => `${o.type_label} : besoin ${o.needed_count}, déjà ${o.committed_count} envoyé(s)/confirmé(s)`);
                alert(`Attention, le besoin est en dessous de ce qui est déjà engagé auprès du partenaire (rien n'a été annulé automatiquement) :\n\n${lines.join('\n')}`);
            }
            onSaved();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    const eventLabel = event.type === 'PRIVÉ' ? `${event.first_name || ''} ${event.last_name || ''}`.trim() : (event.company_name || '');

    return (
        <Modal open onClose={onClose} title={`Booker les extras — ${eventLabel}`} icon={<UserCheck size={16} />} maxWidth="max-w-lg"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Annuler</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving || loading}>{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
                </>
            }>
            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
            ) : typesWithNeed.length === 0 && internalTypesWithNeed.length === 0 ? (
                <p className="text-sm text-content-muted">Aucun besoin en extras pour cet événement.</p>
            ) : (
                <div className="space-y-5">
                    {/* Dit d'entrée ce que le booker peut et ne peut pas faire : sans cette phrase, voir des
                        quantités et des horaires figés se lit comme une panne, pas comme une règle. */}
                    <p className="text-[11px] text-content-muted leading-relaxed p-2.5 rounded-xl bg-surface-soft border border-border">
                        Les quantités et les horaires sont fixés par la personne qui a demandé le booking, depuis
                        « Modifier la Privatisation » — ils ne se changent pas ici. Vous choisissez qui vient :
                        vous réservez les salariés internes, et vous recommandez les externes au partenaire.
                    </p>
                    {Array.from(new Set([...typesWithNeed.map((t) => t.id), ...internalTypesWithNeed.map((t) => t.id)])).map((typeId) => {
                        const flatType = allExtraTypesFlat.find((t) => t.id === typeId);
                        const internalType = extrasInternalTypes.find((t) => t.id === typeId);
                        const label = flatType?.label || internalType?.label || '';
                        const needed = getNeededCount(typeId);
                        const remaining = remainingForType(typeId);
                        const selectedEmployeeIds = extrasSelections.find((s) => s.kind === 'internal' && s.internalTypeId === typeId)?.employeeIds || [];
                        const selectedPersonIds = extrasSelections.find((s) => s.kind === 'external' && s.extraTypeId === typeId && !s.slotUid)?.personIds || [];
                        const people = extrasPeopleCache[typeId] || [];
                        const committed = committedExternalByType[typeId] || 0;
                        const timeOverride = extrasTimeOverrides[typeId];
                        const typeSlots = slotsForType(typeId);
                        const dQty = defaultSlotQty(typeId);
                        return (
                            <div key={typeId} className="space-y-2.5 pb-4 border-b border-border last:border-0 last:pb-0">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-bold text-content">{label}</p>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-surface-soft text-content-muted">{needed - remaining}/{needed} couvert{needed - remaining > 1 ? 's' : ''}</span>
                                </div>
                                {/* TOUS les horaires du poste, jamais le seul principal. Avec « 2 hôtesses de 12h à
                                    minuit + 2 de minuit à 4h », l'en-tête n'annonçait que le premier et taisait le
                                    second : le booker ne pouvait pas savoir que la moitié du groupe arrive à minuit.
                                    La quantité accompagne chaque horaire, sinon « 12:00 → 00:00 » ne dit pas combien
                                    de personnes sont attendues à ce moment-là. Signalé par Emeni (2026-08-26). */}
                                {(() => {
                                    const horaires = [
                                        { qty: demandeParDefaut(typeId), start: timeOverride?.start || '', end: timeOverride?.end || '' },
                                        ...typeSlots.map((s) => ({ qty: s.qty || 0, start: s.start, end: s.end })),
                                    ].filter((h) => h.qty > 0 && (h.start || h.end));
                                    if (horaires.length === 0) return null;
                                    return (
                                        <p className="text-[10px] text-content-muted flex flex-wrap items-baseline gap-x-1.5">
                                            <span>{horaires.length > 1 ? 'Horaires demandés :' : 'Horaire demandé :'}</span>
                                            {horaires.map((h, i) => (
                                                <span key={i} className="whitespace-nowrap">
                                                    <span className="font-bold text-content">{h.qty}</span> × {h.start || '?'} → {h.end || '?'}
                                                    {i < horaires.length - 1 && <span className="text-content-muted">,</span>}
                                                </span>
                                            ))}
                                        </p>
                                    );
                                })()}

                                {/* ── Un bloc par CRÉNEAU ────────────────────────────────────────────────
                                    L'écran séparait « extras interne » d'un côté et « extras externe » de
                                    l'autre, les créneaux n'existant que du côté externe. On sélectionnait donc
                                    des salariés sans jamais dire à quelle heure ils venaient, et rien
                                    n'empêchait d'en mettre quatre sur un créneau qui n'en demandait que deux.
                                    « On ne comprend rien de ce qui est demandé » (Emeni, 2026-08-28).
                                    Le créneau devient l'unité : il porte sa quantité, ses internes, puis ce
                                    qu'il reste à demander au prestataire. */}
                                {(() => {
                                    const creneaux = [
                                        { uid: undefined as string | undefined, qty: demandeParDefaut(typeId), start: timeOverride?.start || eventStartTime, end: timeOverride?.end || eventEndTime },
                                        ...typeSlots.map((s) => ({ uid: s.uid as string | undefined, qty: s.qty || 0, start: s.start, end: s.end })),
                                    ].filter((cr) => cr.qty > 0);
                                    if (creneaux.length === 0) return null;
                                    return creneaux.map((cr) => {
                                        const internesIci = internesDuCreneau(typeId, cr.uid);
                                        const externesIci = extrasSelections.find((s) => s.kind === 'external' && s.extraTypeId === typeId && (s.slotUid || undefined) === (cr.uid || undefined))?.personIds || [];
                                        const resteIci = Math.max(0, cr.qty - internesIci.length - externesIci.length);
                                        const aUnConflitVisible = !!internalType && internalType.employees.some((e) => !internesIci.includes(e.employee_id) && creneauEnConflit(e.employee_id, typeId, cr.uid));
                                        return (
                                            <div key={cr.uid || 'defaut'} className="space-y-2 p-2.5 rounded-xl bg-surface-soft border border-border">
                                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                                    <span className="text-xs font-bold text-content">
                                                        {libelleCreneau(cr.start, cr.end) || 'Horaire non précisé'} · {cr.qty} personne{cr.qty > 1 ? 's' : ''}
                                                    </span>
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${resteIci === 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`}>
                                                        {resteIci === 0 ? 'complet' : `${resteIci} à pourvoir`}
                                                    </span>
                                                </div>

                                                {internalType && (
                                                    <div className="space-y-1">
                                                        <p className="text-[9px] font-bold text-content-muted uppercase tracking-widest">En interne</p>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {internalType.employees.map((e) => {
                                                                const isSelected = internesIci.includes(e.employee_id);
                                                                const conflit = isSelected ? null : creneauEnConflit(e.employee_id, typeId, cr.uid);
                                                                // Le plafond porte sur CE créneau, plus sur le besoin total du poste.
                                                                const plafondAtteint = !isSelected && internesIci.length >= cr.qty;
                                                                const disabled = !!conflit || plafondAtteint;
                                                                return (
                                                                    <button type="button" key={e.employee_id} disabled={disabled}
                                                                        title={conflit ? `Déjà sur ${conflit} — ces heures se recoupent` : undefined}
                                                                        onClick={() => toggleInternalEmployee(typeId, e.employee_id, cr.uid)}
                                                                        className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${isSelected ? 'bg-brand-600 border-brand-600 text-brand-fg' : conflit ? 'bg-surface-soft border-border text-content-muted opacity-40 cursor-not-allowed line-through' : disabled ? 'bg-surface-soft border-border text-content-muted opacity-40 cursor-not-allowed' : 'bg-surface-soft border-border text-content hover:border-brand-500/40'}`}>
                                                                        {`${e.first_name} ${e.last_name}`.trim()}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                        {aUnConflitVisible && (
                                                            <p className="text-[10px] text-content-muted italic">Les personnes barrées sont déjà prises sur un horaire qui recoupe celui-ci.</p>
                                                        )}
                                                    </div>
                                                )}

                                                {resteIci > 0 && (
                                                    <div className="space-y-1">
                                                        <p className="text-[9px] font-bold text-content-muted uppercase tracking-widest">
                                                            À demander au prestataire — {resteIci} restant{resteIci > 1 ? 's' : ''}
                                                        </p>
                                                        {people.length === 0 ? (
                                                            <p className="text-[10px] text-content-muted italic">Aucune personne enregistrée pour ce type — depuis la fiche du partenaire.</p>
                                                        ) : (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {people.map((pers) => {
                                                                    const isSelected = externesIci.includes(pers.id);
                                                                    const disabled = !isSelected && externesIci.length >= Math.max(0, cr.qty - internesIci.length);
                                                                    return (
                                                                        <button type="button" key={pers.id} disabled={disabled} onClick={() => toggleExternalPerson(typeId, flatType?.partnerId || '', pers.id, cr.uid)}
                                                                            className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${isSelected ? 'bg-brand-600 border-brand-600 text-brand-fg' : disabled ? 'bg-surface-soft border-border text-content-muted opacity-40 cursor-not-allowed' : 'bg-surface-soft border-border text-content hover:border-brand-500/40'}`}>
                                                                            {pers.first_name} {pers.last_name}{pers.already_used ? ' ·' : ''}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        );
                    })}

                    <label className="flex items-center gap-2.5 cursor-pointer pt-2">
                        <input type="checkbox" checked={autoSendExtras} onChange={(e) => setAutoSendExtras(e.target.checked)}
                            className="w-4 h-4 rounded border-border accent-brand-600" />
                        <span className="text-xs text-content">Envoi auto. des demandes aux prestataires une fois les extras bookés</span>
                    </label>

                    {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                </div>
            )}
        </Modal>
    );
}
